import { isNumber } from '@art-suite/art-core-ts';
import { InlineProductsById } from '../CommonScanSchemas';
import { Scope } from '../OptimizedValueExpressions';
import { getEffectiveScanValue } from '../ScanRoot';
import { initLineItemFromTemplate, initOptionFromTemplate } from './initScanFromTemplate';
import { getScopeForScanPartChildren } from './ScanOptimizedGetters';
import { getUpdatedLineItem, getUpdatedOption, getUpdatedTask } from './ScanOptimizedPureFunctionalUpdates';
import { ScanGroupOptimized, ScanLineItemOptimized, ScanOptionOptimized, ScanTaskOptimized } from './ScanRootOptimizedSchemas';

/** Whether a line item with a quantity value has none above zero to cost. */
const lineItemHasZeroQuantity = (lineItem: ScanLineItemOptimized): boolean => {
  if (!lineItem.template.values?.quantity) return false;
  const quantity = getEffectiveScanValue(lineItem.values?.quantity);
  return !(isNumber(quantity) && quantity > 0);
};

/**
 * A preview costs what adding the line item will: a measured line item goes in as it is, and any
 * other at its default quantity when that's above zero, otherwise at one.
 */
const getLineItemPreview = (
  groupId: string,
  taskId: string,
  optionId: string,
  lineItem: ScanLineItemOptimized,
  scopeForLineItems: Scope,
  now: number,
): ScanLineItemOptimized => {
  if (lineItem.template.values?.measurement || !lineItemHasZeroQuantity(lineItem)) return lineItem;
  const quantity = lineItem.values.quantity;
  return getUpdatedLineItem(
    groupId,
    taskId,
    optionId,
    lineItem,
    { values: { quantity: { ...quantity, setValue: 1 } } },
    scopeForLineItems,
    now,
  );
};

/**
 * Given an option, return it with any line-item templates it hasn't instantiated vivified, in the
 * option template's lineItemTemplates order, each previewed as it would be added (see
 * getLineItemPreview). A line item that exists is in the scope and is left alone, unless the whole
 * option is itself a preview; when nothing changes the same option instance is returned.
 */
const getOptionWithPreviewLineItems = (
  groupId: string,
  taskId: string,
  option: ScanOptionOptimized,
  optionIsPreview: boolean,
  scopeForOption: Scope,
  now: number,
  inlineProductsById?: InlineProductsById,
): ScanOptionOptimized => {
  const scopeForLineItems = getScopeForScanPartChildren(option);
  const previewLineItem = (lineItem: ScanLineItemOptimized) => getLineItemPreview(groupId, taskId, option.id, lineItem, scopeForLineItems, now);
  const lineItems = (option.template.lineItemTemplates ?? []).flatMap(lineItemTemplate => {
    const existingLineItems = option.lineItems?.filter(lineItem => lineItem.templateId === lineItemTemplate.id) ?? [];
    if (existingLineItems.length === 0) return [previewLineItem(initLineItemFromTemplate(lineItemTemplate, scopeForLineItems, now, inlineProductsById))];
    return optionIsPreview ? existingLineItems.map(previewLineItem) : existingLineItems;
  });
  if (lineItems.length === (option.lineItems?.length ?? 0) && lineItems.every((lineItem, index) => lineItem === option.lineItems?.[index])) {
    return option;
  }
  // re-optimized, so the option's own totals include its previewed line items
  return getUpdatedOption(groupId, taskId, option, { lineItems }, scopeForOption, now);
};

/**
 * Given a task, return a new task with every part of its template vivified: missing options
 * (by option template-id) are fully instantiated, and existing options gain instances for any
 * line-item templates they haven't instantiated yet (by line-item template-id). Each vivified
 * line item previews at the quantity adding it will set (see getLineItemPreview). Existing
 * options and line items are reused untouched, so preview parts are exactly the ones whose ids
 * don't appear in the real task.
 * Options are returned in the task template's optionTemplates order, and each option's line
 * items in its template's lineItemTemplates order.
 * With `inlineProductsById`, a vivified single-SKU line item is seeded from its product (details
 * and price) exactly as it would be when added, so the preview costs what the real one will.
 * @param task - The task to get the preview of.
 * @returns The preview of the task, or the same task instance when nothing is missing.
 */
export const getTaskOptimizedPreview = (
  group: ScanGroupOptimized,
  task: ScanTaskOptimized,
  now: number = Date.now(),
  inlineProductsById?: InlineProductsById,
): ScanTaskOptimized => {
  const scopeForTask = getScopeForScanPartChildren(group);
  const scopeForOptions = getScopeForScanPartChildren(task);
  const options = (task.template.optionTemplates ?? []).flatMap(optionTemplate => {
    const existingOptions = task.options.filter(option => option.templateId === optionTemplate.id);
    const optionIsPreview = existingOptions.length === 0;
    return (optionIsPreview ? [initOptionFromTemplate(optionTemplate, scopeForOptions, now, inlineProductsById)] : existingOptions)
      .map(option => getOptionWithPreviewLineItems(group.id, task.id, option, optionIsPreview, scopeForOptions, now, inlineProductsById));
  });
  if (options.length === task.options.length && options.every((option, index) => option === task.options[index])) return task;
  return getUpdatedTask(group.id, task, { options }, scopeForTask, now);
};
